import { ThemeOverride } from '@components/integrated/theme-override';
import type { ThemeOverrideConfig } from '@components/integrated/theme-override';
import { VStack, Card } from '@components/base/layout';
import { Text, Badge } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Tabs } from '@components/base/navigation';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

// ---------------------------------------------------------------------------
// Helper: Code block for displaying configuration
// ---------------------------------------------------------------------------

function ConfigCodeBlock({ config }: Readonly<{ config: ThemeOverrideConfig }>) {
  return (
    <pre className="bg-[#1E293B] text-[#E2E8F0] text-xs leading-relaxed p-4 rounded-lg overflow-x-auto">
      <code>{JSON.stringify(config, null, 2)}</code>
    </pre>
  );
}

// ---------------------------------------------------------------------------
// Theme config presets
// ---------------------------------------------------------------------------

const roseTheme: ThemeOverrideConfig = {
  id: 'rose-theme',
  colors: {
    primary: {
      50: '#fff1f2', 100: '#ffe4e6', 200: '#fecdd3', 300: '#fda4af',
      400: '#fb7185', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c',
      800: '#9f1239', 900: '#881337', 950: '#4c0519',
    },
  },
};

const violetAccentTheme: ThemeOverrideConfig = {
  id: 'violet-accent',
  colors: {
    accent: {
      50: '#f5f3ff', 100: '#ede9fe', 200: '#ddd6fe', 300: '#c4b5fd',
      400: '#a78bfa', 500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9',
      800: '#5b21b6', 900: '#4c1d95', 950: '#2e1065',
    },
  },
};

const emeraldTheme: ThemeOverrideConfig = {
  id: 'emerald-theme',
  colors: {
    primary: {
      50: '#ecfdf5', 100: '#d1fae5', 200: '#a7f3d0', 300: '#6ee7b7',
      400: '#34d399', 500: '#10b981', 600: '#059669', 700: '#047857',
      800: '#065f46', 900: '#064e3b', 950: '#022c22',
    },
  },
};

const amberTheme: ThemeOverrideConfig = {
  id: 'amber-theme',
  colors: {
    primary: {
      50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 300: '#fcd34d',
      400: '#fbbf24', 500: '#f59e0b', 600: '#d97706', 700: '#b45309',
      800: '#92400e', 900: '#78350f', 950: '#451a03',
    },
  },
};

const georgiaTheme: ThemeOverrideConfig = {
  id: 'georgia-typography',
  typography: {
    fontSans: '"Georgia", "Times New Roman", serif',
  },
};

/** Map playground palette names to config objects. */
const paletteMap: Record<string, ThemeOverrideConfig | undefined> = {
  default: undefined,
  rose: roseTheme,
  violet: violetAccentTheme,
  emerald: emeraldTheme,
  amber: amberTheme,
};

// ---------------------------------------------------------------------------
// Nesting configs
// ---------------------------------------------------------------------------

const nestingOuterConfig: ThemeOverrideConfig = {
  id: 'nesting-outer',
  colors: {
    primary: roseTheme.colors!.primary,
  },
};

const nestingInnerConfig: ThemeOverrideConfig = {
  id: 'nesting-inner',
  colors: {
    accent: violetAccentTheme.colors!.accent,
  },
};

// ---------------------------------------------------------------------------
// Color Palette demo: combined config
// ---------------------------------------------------------------------------

const colorPaletteDemoConfig: ThemeOverrideConfig = {
  id: 'color-palette-demo',
  colors: {
    primary: roseTheme.colors!.primary,
    accent: violetAccentTheme.colors!.accent,
  },
};

// ---------------------------------------------------------------------------
// Remote Configuration Usage Code
// ---------------------------------------------------------------------------

const remoteConfigUsageCode = `// Remote configuration mode
// The theme config is fetched from the API endpoint:
// GET /api/{module}/config → { data: { config: { "brand-theme": {...} } } }

<ThemeOverride remoteConfigId="brand-theme">
  <App />
</ThemeOverride>

// Optional props:
// - tenant="custom-tenant"        → Override tenant from context
// - module="custom-module"        → Override module from context
// - loadingFallback={<MyLoader />} → Custom loading UI
// - errorMessage="Custom error"    → Custom error message
// - as="section"                   → Change the wrapper element
// - className="my-class"           → Add CSS classes to wrapper

// Prerequisites:
// Wrap your app with IntegratedComponentProvider:
<IntegratedComponentProvider
  apiClient={axiosInstance}
  tenant="tenant-id"
  module="my-module"
>
  <App />
</IntegratedComponentProvider>`;

// ---------------------------------------------------------------------------
// Sample Card -- renders inside ThemeOverride scopes
// ---------------------------------------------------------------------------

function SampleCard({ label }: Readonly<{ label?: string }>) {
  return (
    <Card className="p-4 max-w-xs">
      <VStack gap={3}>
        {label && <Text size="xs" color="muted">{label}</Text>}
        <Text size="lg" weight="semibold" className="text-primary-800">
          Sample Card
        </Text>
        <Text size="sm" className="text-primary-600">
          This card uses semantic token classes that respond to ThemeOverride.
        </Text>
        <div className="flex gap-2 items-center">
          <Badge variant="primary">Primary</Badge>
          <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium uppercase tracking-wider rounded bg-accent-100 text-accent-800">
            Accent
          </span>
        </div>
        <Button variant="primary" size="sm">Primary Action</Button>
      </VStack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Showcase Component
// ---------------------------------------------------------------------------

function ThemeOverrideShowcaseComponent({
  playgroundProps,
  isDarkMode: _isDarkMode,
}: Readonly<ShowcaseProps>) {
  const paletteName =
    (playgroundProps.palette as string) ?? 'default';

  const selectedConfig = paletteMap[paletteName];

  return (
    <VStack gap={6}>
      {/* ---- Interactive Preview ---- */}
      <ShowcasePreviewSection componentName="theme override">
        <div className="w-full">
          {selectedConfig ? (
            <ThemeOverride config={selectedConfig}>
              <SampleCard label={`Palette: ${paletteName}`} />
            </ThemeOverride>
          ) : (
            <SampleCard label="Palette: default (no override)" />
          )}
        </div>
      </ShowcasePreviewSection>

      {/* ---- Color Palette Override ---- */}
      <ShowcaseDemoSection
        title="Color Palette Override"
        description="Side-by-side comparison of the default theme versus a ThemeOverride that replaces primary with rose and accent with violet"
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <div className="flex flex-wrap gap-6">
              <SampleCard label="Default theme" />
              <ThemeOverride config={colorPaletteDemoConfig}>
                <SampleCard label="Rose primary + Violet accent" />
              </ThemeOverride>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <ConfigCodeBlock config={colorPaletteDemoConfig} />
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Typography Override ---- */}
      <ShowcaseDemoSection
        title="Typography Override"
        description="Override the default sans-serif font stack with Georgia serif for a different typographic feel"
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <div className="flex flex-wrap gap-6">
              <SampleCard label="Default typography" />
              <ThemeOverride config={georgiaTheme}>
                <SampleCard label="Georgia serif" />
              </ThemeOverride>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <ConfigCodeBlock config={georgiaTheme} />
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Nesting ---- */}
      <ShowcaseDemoSection
        title="Nesting"
        description="Outer ThemeOverride sets primary to rose. Inner ThemeOverride adds accent violet. The inner scope inherits the outer primary while overriding only accent."
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <ThemeOverride config={nestingOuterConfig}>
              <VStack gap={4}>
                <SampleCard label="Outer: rose primary" />
                <ThemeOverride config={nestingInnerConfig}>
                  <SampleCard label="Inner: rose primary (inherited) + violet accent" />
                </ThemeOverride>
              </VStack>
            </ThemeOverride>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <Text size="sm" weight="medium" className="mb-2">Outer Config</Text>
                <ConfigCodeBlock config={nestingOuterConfig} />
              </div>
              <div>
                <Text size="sm" weight="medium" className="mb-2">Inner Config</Text>
                <ConfigCodeBlock config={nestingInnerConfig} />
              </div>
            </div>
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Remote Configuration ---- */}
      <ShowcaseDemoSection
        title="Remote Configuration"
        description="ThemeOverride can fetch its configuration from the remote API endpoint (GET /api/{module}/config) using the remoteConfigId prop. The IntegratedComponentProvider supplies the API client, tenant and module."
       
      >
        <pre className="bg-[#1E293B] text-[#E2E8F0] text-xs leading-relaxed p-4 rounded-lg overflow-x-auto">
          <code>{remoteConfigUsageCode}</code>
        </pre>
      </ShowcaseDemoSection>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Showcase Definition
// ---------------------------------------------------------------------------

export const ThemeOverrideShowcase: ShowcaseDefinition = {
  id: 'theme-override',
  name: 'ThemeOverride',
  category: 'theming',
  description:
    'Scoped CSS custom property overrides for colors, typography, border radii, and shadows with nesting and remote config support',
  propsDefinition: [
    {
      name: 'palette',
      type: 'select',
      options: ['default', 'rose', 'violet', 'emerald', 'amber'],
      defaultValue: 'default',
      description: 'Primary color palette preset',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'No ARIA attributes needed — ThemeOverride is a transparent styling wrapper',
    ],
    keyboardNav: [
      'No keyboard interaction — ThemeOverride does not affect focus or navigation',
    ],
    notes: [
      'ThemeOverride applies CSS custom properties via inline style on a wrapper div',
      'All child components maintain their existing accessibility features',
      'Color contrast ratios should be verified when overriding color palettes',
    ],
  },
  component: ThemeOverrideShowcaseComponent,
};
